import { HelpArticle, HelpCategory } from '../types';

interface ZendeskApiArticle {
  id: number;
  title: string;
  html_url: string;
  body?: string;
  promoted?: boolean;
}

interface ZendeskCategoriesResponse {
  categories: { id: number; name: string; html_url: string }[];
}

interface ZendeskSearchResponse {
  results: ZendeskApiArticle[];
  next_page: string | null;
  articles?: ZendeskApiArticle[];
}

interface ZendeskArticleResponse {
  article: ZendeskApiArticle;
}

interface ZendeskSectionsResponse {
  sections: { id: number; name: string; html_url: string; parent_section_id: number }[];
}

type Params = Record<string, string | number>;

interface APIFetchOptions {
  signal?: AbortSignal;
  params?: Params;
  locale?: string;
}

const fetchFromHC = async <T>(endpoint: string, options: APIFetchOptions): Promise<T> => {
  const { signal, params } = options;

  if (params) {
    const searchParams = new URLSearchParams(
      Object.entries(params).map(([key, value]) => [key, String(value)]),
    );
    endpoint += `?${searchParams}`;
  }

  try {
    const response = await fetch(endpoint, { signal });
    if (!response.ok) {
      throw new Error(`API call failed with status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      throw error;
    }
    console.error(`Error fetching from endpoint "${endpoint}":`, error);
    throw new Error(`Failed to fetch data for endpoint "${endpoint}"`);
  }
};

const fetchPaginatedEndpoint = async <T>(
  endpoint: string,
  options: APIFetchOptions,
): Promise<T[]> => {
  let allItems: T[] = [];
  let currentUrl: string | null = null;
  let initialResponse: any = await fetchFromHC(endpoint, options);

  const items = initialResponse.results || initialResponse.articles || [];
  allItems = [...allItems, ...items];
  currentUrl = initialResponse.next_page;

  while (currentUrl) {
    try {
      const response = await fetch(currentUrl, { signal: options.signal });
      if (!response.ok) {
        throw new Error(`API call failed with status: ${response.status}`);
      }
      const data = await response.json();
      allItems = [...allItems, ...(data.results || data.articles || [])];
      currentUrl = data.next_page;
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        throw error;
      }
      console.error(`Error fetching paginated data from "${currentUrl}":`, error);
      throw new Error(`Failed to fetch paginated data`);
    }
  }

  return allItems;
};

const mapArticle = (article: ZendeskApiArticle): HelpArticle => ({
  id: String(article.id),
  title: article.title,
  url: article.html_url,
  content: article.body || '',
});

const mapCategory = (category: { id: number; name: string; html_url: string }): HelpCategory => ({
  id: String(category.id),
  title: category.name,
  url: category.html_url,
});

const mapSection = (section: {
  id: number;
  name: string;
  html_url: string;
  parent_section_id?: number;
}): HelpCategory => ({
  id: String(section.id),
  title: section.name,
  url: section.html_url,
  parentSectionId: section.parent_section_id ? String(section.parent_section_id) : undefined,
});

export const vimeoHCClient = {
  getHomeData: async (hc_base_url: string, locale: string, signal?: AbortSignal) => {
    const [categoriesData, featuredArticlesData] = await Promise.all([
      fetchFromHC<ZendeskCategoriesResponse>(
        `${hc_base_url}/api/v2/help_center/${locale}/categories.json`,
        {
          locale,
          signal,
        },
      ),
      fetchPaginatedEndpoint<ZendeskApiArticle>(
        `${hc_base_url}/api/v2/help_center/articles/search.json`,
        {
          signal,
          params: { per_page: 100, label_names: 'featured', 'filter[locales]': locale },
        },
      ),
    ]);

    const promotedArticles = featuredArticlesData
      .filter((article) => article.promoted)
      .map(mapArticle);

    return {
      categories: categoriesData.categories.map(mapCategory),
      featured: promotedArticles,
    };
  },

  getArticleById: async (
    hc_base_url: string,
    locale: string,
    articleId: string,
    signal?: AbortSignal,
  ) => {
    const articleData = await fetchFromHC<ZendeskArticleResponse>(
      `${hc_base_url}/api/v2/help_center/${locale}/articles/${articleId}.json`,
      { locale, signal },
    );
    return mapArticle(articleData.article) as HelpArticle;
  },

  getArticlesBySectionId: async (
    hc_base_url: string,
    locale: string,
    sectionId: string,
    signal?: AbortSignal,
  ) => {
    const articlesData = await fetchFromHC<ZendeskSearchResponse>(
      `${hc_base_url}/api/v2/help_center/${locale}/sections/${sectionId}/articles.json`,
      {
        locale,
        signal,
      },
    );
    return articlesData.articles?.map(mapArticle) as HelpArticle[];
  },

  getSectionsByCategoryId: async (
    hc_base_url: string,
    locale: string,
    categoryId: string,
    signal?: AbortSignal,
  ) => {
    const sectionsData = await fetchFromHC<ZendeskSectionsResponse>(
      `${hc_base_url}/api/v2/help_center/${locale}/categories/${categoryId}/sections.json`,
      {
        locale,
        signal,
      },
    );
    return sectionsData.sections.map(mapSection);
  },

  searchArticles: async (
    hc_base_url: string,
    locale: string,
    query: string,
    signal?: AbortSignal,
  ) => {
    const searchData = await fetchPaginatedEndpoint<ZendeskApiArticle>(
      `${hc_base_url}/api/v2/help_center/articles/search.json`,
      {
        signal,
        params: { query, per_page: 100, 'filter[locales]': locale },
      },
    );
    return searchData.map(mapArticle);
  },
};
